import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import PaymentStatusTag from 'components/PaymentStatusTag';
import {default as Modal, withModal} from 'components/Modal';

import {
  BaseInformation,
  TransactionsTable,
  Total,
  ParentObject
} from './components';

import {
  SCHEDULE_ITEM_TYPES,
  MODALS,
  FETCH_SCHEDULE_DELAY,
  fetchPayment,
  voidObject,
  syncWithXero,
  addXeroId
} from 'ducks/tenancy';
import {delayedAction, composeActions} from 'ducks/common';
import {
  MODALS as BASE_MODALS,
  openModal,
  closeModal,
  storeDataInModal
} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import './styles.css';

class RefundDetailModal extends React.Component<any> {
  componentDidMount() {
    const {data: {refundId, locationRef}} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        const fetchRefundExtraActions = this.props.storeDataExtraActions(
          data => ({
            refund: data
          })
        );

        this.props.fetchPayment({paymentId: refundId}, fetchRefundExtraActions);
      }
    );
  }

  openConfirmationModal = ({heading, confirmAction, question}) =>
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading,
      question,
      confirmAction
    });

  getSuccessExtraActions = () => {
    return [
      ...this.props.closeModalExtraActions,
      ...this.props.refetchExtraActions({
        tenancyId: this.props.data.tenancyId,
        scheduleType: this.props.data.scheduleType
      })
    ];
  };

  delayedFetchScheduleAction = () =>
    delayedAction({
      delay: FETCH_SCHEDULE_DELAY,
      action: () =>
        composeActions(
          this.props.delayExtraActions({
            tenancyId: this.props.data.tenancyId,
            scheduleType: this.props.data.scheduleType
          })
        )
    });

  voidTransactionConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.voidObject(
      {
        objectId: this.props.data.refund.transaction.id,
        objectType: SCHEDULE_ITEM_TYPES.REFUND,
        scheduleType: this.props.data.scheduleType,
        tenancyId: this.props.data.tenancyId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  voidTransaction = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VOID_SCHEDULE_ITEMS
      },
      () =>
        this.openConfirmationModal({
          heading: 'Void Refund',
          question: 'Are you sure you want to void this refund?',
          confirmAction: this.voidTransactionConfirmAction
        })
    );

  syncWithXeroConfirmAction = () => {
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ...this.getSuccessExtraActions(),
      this.delayedFetchScheduleAction
    ];
    const failureExtraAction = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.syncWithXero(
      {
        objectId: this.props.data.refundId,
        objectType: SCHEDULE_ITEM_TYPES.REFUND,
        scheduleType: this.props.data.scheduleType,
        tenancyId: this.props.data.tenancyId
      },
      successExtraActions,
      failureExtraAction
    );
  };

  syncRefund = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.SYNC_WITH_XERO
      },
      () =>
        this.openConfirmationModal({
          heading: 'Sync Refund',
          question: 'Are you sure you want to sync this refund with xero?',
          confirmAction: this.syncWithXeroConfirmAction
        })
    );

  addXeroIdToRefund = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.ADD_XERO_ID
      },
      () => {
        const xeroId = window.prompt('Xero ID: ', '');
        if (!_.isEmpty(xeroId)) {
          const successExtraActions = [
            ...this.getSuccessExtraActions(),
            this.delayedFetchScheduleAction
          ];
          const failureExtraActions = this.props.storeDataExtraActions(
            ({errors}) => ({errors})
          );
          this.props.addXeroId(
            {
              xeroId,
              objectId: this.props.data.refundId,
              objectType: SCHEDULE_ITEM_TYPES.REFUND,
              scheduleType: this.props.data.scheduleType,
              tenancyId: this.props.data.tenancyId
            },
            successExtraActions,
            failureExtraActions
          );
        }
      }
    );

  onParentObjectClick = parentObject =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        // Since we do not support opening a related Overpayment
        // We just open the related credit note
        const {scheduleType, locationRef, tenancyId} = this.props.data;

        this.props.composeActions([
          () => closeModal(MODALS.REFUND_DETAIL),
          () =>
            openModal(
              MODALS.CREDIT_NOTE_DETAIL,
              {
                scheduleType,
                creditNoteId: parentObject.id,
                locationRef,
                tenancyId
              },
              true
            )
        ]);
      }
    );

  render() {
    const refund = _.get(this.props, 'data.refund');
    const creditNote = _.get(refund, 'creditNote');
    const overpayment = _.get(refund, 'overpayment');
    const errors = _.get(this.props, 'data.errors', {});

    const parentObject = creditNote || overpayment;
    const parentObjectLabel =
      (creditNote && 'Credit note') || (overpayment && 'Overpayment');

    const xeroSyncErrors = _.get(this.props, 'data.refund.xeroErrors', []);

    return (
      <div>
        {refund && (
          <Modal
            show={true}
            onClose={this.props.closeModal}
            className={classNames('NewRefundDetailModal', {
              statusPadded: refund && refund.voided
            })}>
            <Modal.Header>
              <h3 className="header">Refund</h3>
              {refund &&
                refund.voided && (
                  <div className="status">
                    <PaymentStatusTag status={PaymentStatusTag.VOIDED} />
                  </div>
                )}
            </Modal.Header>
            <Modal.Body>
              <Modal.Errors errors={errors} />
              <Modal.Errors errors={xeroSyncErrors} />

              <BaseInformation
                refund={refund}
                voidTransaction={this.voidTransaction}
                syncRefund={this.syncRefund}
                addXeroIdToRefund={this.addXeroIdToRefund}
              />
              <ParentObject>{parentObjectLabel}</ParentObject>
              <TransactionsTable
                refund={refund}
                onParentObjectClick={this.onParentObjectClick}
                parentObject={parentObject}
                parentObjectLabel={parentObjectLabel}
              />
              <Total total={refund.amount} />
            </Modal.Body>
          </Modal>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = {
  fetchPayment,
  syncWithXero,
  voidObject,
  addXeroId,
  requiresPermission,
  delayedAction,
  openModal,
  composeActions
};

export default connect(null, mapDispatchToProps)(withModal(
  MODALS.REFUND_DETAIL
)(RefundDetailModal) as React.ComponentType<any>);
